import React, { lazy, memo, PropsWithChildren, Suspense, useEffect, useMemo, useState } from 'react'
import { ViewType } from '../assets/constants'
import StockBar from './home/stockBar/stockBar'
import { useStyles } from './homeLayoutStyles'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { selectIsEmbedded } from '../selectors/device'
import { selectFinancialPanelOpen } from '../selectors/home'
import store from '../store'
import { useHomeLayoutNavigation } from './home/useHomeLayoutNavigation'
import FinancialDataSkeleton from './financialData/FinancialDataSkeleton'
import { setFinancialPanelOpen } from '../reducer/home.reducer'

const FinancialDataContent = lazy(() => import('./financialData/FinancialDataContent'))

export type HomeLayoutProps = PropsWithChildren & {
    view: ViewType,
    error?: string
}

const FINANCIAL_PANEL_VIEWS = new Set<ViewType>([ViewType.CHART, ViewType.SYMBOL])

const contentAreaStyle = (view: ViewType, background: string): React.CSSProperties =>
    view === ViewType.AUTOMATION
        ? { gridArea: 'content', overflow: 'auto', background }
        : {
              gridArea: 'content',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: '100%',
              background
          }

const HomeLayout: React.FC<HomeLayoutProps> = ({ view, error, children }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const financialPanelOpen = useSelector(selectFinancialPanelOpen)
    const [isEmbedded, setIsEmbedded] = useState(() => selectIsEmbedded(store.getState()))
    const showFinancialPanel = financialPanelOpen && FINANCIAL_PANEL_VIEWS.has(view)
    const stockBarView = showFinancialPanel ? ViewType.FINANCIAL_DATA : view

    useHomeLayoutNavigation(view)

    useEffect(() => {
        if (!FINANCIAL_PANEL_VIEWS.has(view) && financialPanelOpen) {
            dispatch(setFinancialPanelOpen(false))
        }
    }, [dispatch, financialPanelOpen, view])

    useEffect(() => {
        const sync = () => {
            const next = selectIsEmbedded(store.getState())
            setIsEmbedded((prev) => (prev === next ? prev : next))
        }
        return store.subscribe(sync)
    }, [])

    const contentStyle = useMemo(
        () =>
            contentAreaStyle(
                stockBarView,
                view === ViewType.AUTOMATION || showFinancialPanel
                    ? theme.palette.background.paper
                    : theme.palette.background.default,
            ),
        [showFinancialPanel, stockBarView, theme.palette.background.default, theme.palette.background.paper, view],
    )

    return (
        <div style={{ gridArea: 'main-content' }} className={cx(classes.container, 'main-content')}>
            {error ? (
                <Stack
                    className="empty-view"
                    justifyContent="center"
                    alignItems="center"
                    direction="column"
                    spacing={1}
                >
                    <Typography variant="body1">{error}</Typography>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                            navigate({ pathname: '/', search: '' }, { replace: true })
                            window.location.reload()
                        }}
                    >
                        Go To Home
                    </Button>
                </Stack>
            ) : (
                <>
                    {!isEmbedded && (
                        <StockBar
                            viewType={stockBarView}
                            ivPlotTimeBucket={[ViewType.OPEN_INTEREST, ViewType.OPEN_INTEREST_CHANGE, ViewType.MULTI_STRIKES_CHART].includes(view)}
                        />
                    )}
                    <div style={contentStyle}>
                        {showFinancialPanel ? (
                            <Suspense fallback={<FinancialDataSkeleton />}>
                                <FinancialDataContent />
                            </Suspense>
                        ) : (
                            children
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

function homeLayoutPropsAreEqual(prev: HomeLayoutProps, next: HomeLayoutProps): boolean {
    if (prev.view !== next.view || prev.error !== next.error) return false
    // Chart page: children is stable <CashViewContent />; tick/layout state updates inside that subtree.
    if (prev.view === ViewType.CHART) return true
    return prev.children === next.children
}

export default memo(HomeLayout, homeLayoutPropsAreEqual)
